<template>
  <div class="w-full p-6 bg-white rounded shadow md:w-2/3 md:mx-auto lg:w-3/4">
    <div class="flex flex-col gap-3 mb-6 md:flex-row md:items-center md:justify-between">
      <div>
        <p class="text-sm font-semibold tracking-wide text-custom-green uppercase">Identity Verification</p>
        <h3 class="text-2xl font-bold uppercase text-gray-900">Verify your identity</h3>
      </div>
      <span
        v-if="statusLabel"
        class="inline-flex items-center self-start px-3 py-1 text-sm font-semibold rounded-full"
        :class="statusBadgeClass"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div v-if="loading" class="flex justify-center py-10">
      <svg
        class="w-12 h-12 text-custom-green animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>

    <template v-else>
      <div
        v-if="error"
        class="p-4 mb-5 text-red-700 border-l-4 rounded bg-red-50 border-red-400"
        role="alert"
      >
        <p class="font-bold">Something went wrong</p>
        <p>{{ error }}</p>
      </div>

      <div
        v-if="isNotEnabled"
        class="p-5 border-l-4 rounded bg-gray-50 border-gray-400 text-gray-700"
      >
        <p class="font-bold">Identity verification is not available</p>
        <p>Identity verification is not enabled for your account. Contact support if you believe this is a mistake.</p>
      </div>

      <div v-else-if="status === 'ENABLED_NOT_STARTED'" class="space-y-5">
        <p class="text-gray-700">
          Start a secure Veriff session to verify your ID. You will be redirected to Veriff to complete the scan.
        </p>
        <SubmitButton type="button" :isLoading="submitting" @click="handleStart('start')">
          Start Verification
        </SubmitButton>
      </div>

      <div v-else-if="status === 'PENDING'" class="space-y-5">
        <div class="p-5 text-blue-800 border-l-4 rounded bg-blue-50 border-blue-400">
          <p class="font-bold">Complete your verification</p>
          <p>Your Veriff session is ready. Complete verification below, then return here for status updates.</p>
        </div>
        <SubmitButton
          v-if="verificationUrl"
          type="button"
          :isLoading="submitting"
          @click="openVerification"
        >
          Continue Verification
        </SubmitButton>
        <p v-else class="text-gray-700">
          We are waiting for Veriff to update your verification status.
        </p>
        <div id="veriff-root"></div>
      </div>

      <div v-else-if="status === 'IN_REVIEW'" class="p-5 text-blue-800 border-l-4 rounded bg-blue-50 border-blue-400">
        <p class="font-bold">Verification under review</p>
        <p>Veriff is reviewing your submission. This page will refresh automatically until a final result is available.</p>
      </div>

      <div v-else-if="status === 'VERIFIED'" class="p-5 text-green-800 border-l-4 rounded bg-green-50 border-green-400">
        <p class="text-xl font-bold">Identity verified</p>
        <p>Your identity verification is complete.</p>
      </div>

      <div v-else-if="status === 'FAILED'" class="space-y-5">
        <div class="p-5 text-red-700 border-l-4 rounded bg-red-50 border-red-400">
          <p class="font-bold">Verification failed</p>
          <p>Your identity verification was not approved. You can retry with a new Veriff session.</p>
        </div>
        <SubmitButton type="button" :isLoading="submitting" @click="handleStart('retry')">
          Retry Verification
        </SubmitButton>
      </div>

      <div v-else-if="status === 'EXPIRED'" class="space-y-5">
        <div class="p-5 text-yellow-800 border-l-4 rounded bg-yellow-50 border-yellow-400">
          <p class="font-bold">Verification session expired</p>
          <p>Your Veriff session has expired. Start a new session to complete identity verification.</p>
        </div>
        <SubmitButton type="button" :isLoading="submitting" @click="handleStart('retry')">
          Restart Verification
        </SubmitButton>
      </div>

      <div v-else class="p-5 border-l-4 rounded bg-gray-50 border-gray-400 text-gray-700">
        <p class="font-bold">Verification status unavailable</p>
        <p>Please refresh the page or try again later.</p>
      </div>

      <dl v-if="hasStatusDetails" class="grid grid-cols-1 gap-4 pt-6 mt-6 border-t border-gray-200 md:grid-cols-3">
        <div v-if="provider">
          <dt class="text-sm font-semibold text-gray-500 uppercase">Provider</dt>
          <dd class="mt-1 text-gray-900 capitalize">{{ provider }}</dd>
        </div>
        <div v-if="startedAt">
          <dt class="text-sm font-semibold text-gray-500 uppercase">Started</dt>
          <dd class="mt-1 text-gray-900">{{ formatDate(startedAt) }}</dd>
        </div>
        <div v-if="expiresAt">
          <dt class="text-sm font-semibold text-gray-500 uppercase">Expires</dt>
          <dd class="mt-1 text-gray-900">{{ formatDate(expiresAt) }}</dd>
        </div>
        <div v-if="completedAt">
          <dt class="text-sm font-semibold text-gray-500 uppercase">Completed</dt>
          <dd class="mt-1 text-gray-900">{{ formatDate(completedAt) }}</dd>
        </div>
      </dl>
    </template>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import SubmitButton from '../components/SubmitButton.vue'
import {
  getIdentityVerification,
  startIdentityVerification
} from '../api/identityVerification'

const POLLING_STATUSES = ['PENDING', 'IN_REVIEW']
const POLL_INTERVAL_MS = 5000

export default {
  components: { SubmitButton },
  setup () {
    const status = ref(null)
    const isEnabled = ref(false)
    const verificationUrl = ref(null)
    const provider = ref(null)
    const startedAt = ref(null)
    const completedAt = ref(null)
    const expiresAt = ref(null)
    const loading = ref(true)
    const submitting = ref(false)
    const error = ref('')
    const pollInterval = ref(null)

    const applyStatus = (data) => {
      status.value = data.status
      isEnabled.value = data.is_enabled
      verificationUrl.value = data.verification_url
      provider.value = data.provider
      startedAt.value = data.started_at
      completedAt.value = data.completed_at
      expiresAt.value = data.expires_at
    }

    const getErrorMessage = (err) => {
      return (err.response &&
        err.response.data &&
        (err.response.data.error || err.response.data.message)) ||
        err.message ||
        'Please try again.'
    }

    const clearPolling = () => {
      if (pollInterval.value) {
        clearInterval(pollInterval.value)
        pollInterval.value = null
      }
    }

    const shouldPoll = computed(() => POLLING_STATUSES.includes(status.value))

    const startPolling = () => {
      if (!shouldPoll.value || pollInterval.value) {
        return
      }

      pollInterval.value = setInterval(async () => {
        await fetchStatus({ silent: true })
      }, POLL_INTERVAL_MS)
    }

    const syncPolling = () => {
      if (shouldPoll.value) {
        startPolling()
      } else {
        clearPolling()
      }
    }

    const fetchStatus = async ({ silent = false } = {}) => {
      if (!silent) {
        loading.value = true
      }
      error.value = ''

      try {
        const data = await getIdentityVerification()
        applyStatus(data)
      } catch (err) {
        if (err.response && err.response.status === 403) {
          status.value = 'NOT_ENABLED'
          isEnabled.value = false
          error.value = ''
        } else {
          error.value = getErrorMessage(err)
        }
      } finally {
        loading.value = false
        syncPolling()
      }
    }

    const openVerification = () => {
      if (verificationUrl.value) {
        window.location.href = verificationUrl.value
      }
    }

    const handleStart = async (action) => {
      submitting.value = true
      error.value = ''

      try {
        const data = await startIdentityVerification(action)
        applyStatus(data)
        if (data.verification_url) {
          window.location.href = data.verification_url
        } else {
          syncPolling()
        }
      } catch (err) {
        error.value = getErrorMessage(err)
      } finally {
        submitting.value = false
      }
    }

    const isNotEnabled = computed(() => {
      return status.value === 'NOT_ENABLED' || isEnabled.value === false
    })

    const statusLabel = computed(() => {
      const labels = {
        NOT_ENABLED: 'Not Available',
        ENABLED_NOT_STARTED: 'Not Started',
        PENDING: 'Pending',
        IN_REVIEW: 'In Review',
        VERIFIED: 'Verified',
        FAILED: 'Failed',
        EXPIRED: 'Expired'
      }

      return labels[status.value] || ''
    })

    const statusBadgeClass = computed(() => {
      const classes = {
        NOT_ENABLED: 'bg-gray-100 text-gray-700',
        ENABLED_NOT_STARTED: 'bg-gray-100 text-gray-700',
        PENDING: 'bg-blue-100 text-blue-800',
        IN_REVIEW: 'bg-blue-100 text-blue-800',
        VERIFIED: 'bg-green-100 text-green-800',
        FAILED: 'bg-red-100 text-red-700',
        EXPIRED: 'bg-yellow-100 text-yellow-800'
      }

      return classes[status.value] || 'bg-gray-100 text-gray-700'
    })

    const hasStatusDetails = computed(() => {
      return provider.value || startedAt.value || completedAt.value || expiresAt.value
    })

    const formatDate = (value) => {
      if (!value) {
        return ''
      }

      return new Date(value).toLocaleString()
    }

    onMounted(fetchStatus)
    onBeforeUnmount(clearPolling)

    return {
      status,
      isEnabled,
      verificationUrl,
      provider,
      startedAt,
      completedAt,
      expiresAt,
      loading,
      submitting,
      error,
      isNotEnabled,
      statusLabel,
      statusBadgeClass,
      hasStatusDetails,
      handleStart,
      openVerification,
      formatDate
    }
  }
}
</script>
